import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { notifyNetworkError } from '@/utils/notify';
function debugEnabled() {
    try {
        const p = globalThis?.process?.env?.DEBUG_HTTP;
        if (p && String(p).toLowerCase() === 'true')
            return true;
        const w = globalThis?.__ENV__?.DEBUG_HTTP;
        if (w && String(w).toLowerCase() === 'true')
            return true;
    }
    catch { }
    return false;
}
function isElectron() {
    try {
        // Presence of the preload bridge
        return typeof window.electron?.ipcRenderer?.invoke === 'function';
    }
    catch {
        return false;
    }
}
export async function httpFetch(input, init) {
    // Use Capacitor native HTTP on mobile to bypass CORS and handle HTTPS/ATS reliably
    try {
        if (Capacitor?.isNativePlatform?.()) {
            const method = (init?.method || 'GET').toUpperCase();
            const headers = (init?.headers || {});
            const timeout = init?.timeoutMs && Number.isFinite(init.timeoutMs) ? Math.max(1, Math.min(60000, init.timeoutMs)) : undefined;
            // CapacitorHttp uses `data` for JSON bodies
            // If body is a string, send it as `data` too and let server parse
            const data = typeof init?.body === 'undefined' ? undefined : (typeof init?.body === 'string' ? init?.body : init?.body);
            if (debugEnabled()) {
                try {
                    console.log('[HTTP][Capacitor] →', method, input, headers);
                }
                catch { }
            }
            const res = await CapacitorHttp.request({
                url: input,
                method: method,
                headers,
                data,
                timeout: timeout,
                webFetchExtra: {
                // Ensure credentials not included unless explicitly added by headers
                // This mimics our fetch usage
                },
            });
            const status = res.status || 0;
            if (debugEnabled()) {
                try {
                    console.log('[HTTP][Capacitor] ←', method, input, status);
                }
                catch { }
            }
            if (!status || status >= 500) {
                notifyNetworkError('http-cap', {
                    title: status ? '服务不可用' : '网络异常',
                    message: status ? '服务器暂时无法处理请求，请稍后重试。' : '请检查网络连接或代理设置。',
                });
            }
            const textBody = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '');
            const headersOut = {};
            if (res.headers) {
                for (const [k, v] of Object.entries(res.headers))
                    headersOut[k.toLowerCase()] = String(v);
            }
            return {
                ok: status >= 200 && status < 300,
                status,
                headers: headersOut,
                async text() { return textBody; },
                async json() {
                    try {
                        return typeof res.data === 'string' ? JSON.parse(textBody || 'null') : res.data;
                    }
                    catch {
                        return undefined;
                    }
                },
            };
        }
    }
    catch (e) {
        // If Capacitor HTTP fails or not available, fall back to Electron/web paths below
        console.warn('CapacitorHttp fallback to fetch:', e?.message || e);
    }
    if (isElectron()) {
        const method = (init?.method || 'GET').toUpperCase();
        const headers = (init?.headers || {});
        const body = typeof init?.body === 'string' ? init?.body : init?.body ? JSON.stringify(init.body) : undefined;
        if (debugEnabled()) {
            try {
                console.log('[HTTP][Electron] →', method, input, headers);
            }
            catch { }
        }
        const res = await window.electron.ipcRenderer.invoke('http:fetch', {
            url: input,
            method,
            headers,
            body: body ?? null,
            timeoutMs: init?.timeoutMs,
        });
        const payload = res;
        if (debugEnabled()) {
            try {
                console.log('[HTTP][Electron] ←', method, input, payload.status);
            }
            catch { }
        }
        if (!payload.ok && (!payload.status || payload.status >= 500)) {
            notifyNetworkError('http-el', {
                title: payload.status ? '服务不可用' : '网络异常',
                message: payload.status ? '服务器暂时无法处理请求，请稍后重试。' : '请检查网络连接或代理设置。',
            });
        }
        return {
            ok: payload.ok,
            status: payload.status,
            headers: payload.headers || {},
            async text() {
                return payload.body ?? '';
            },
            async json() {
                const t = payload.body ?? '';
                return t ? JSON.parse(t) : undefined;
            },
        };
    }
    // Web fallback
    let r;
    try {
        if (debugEnabled()) {
            try {
                console.log('[HTTP][Web] →', (init?.method || 'GET').toUpperCase(), input, init?.headers || {});
            }
            catch { }
        }
        r = await fetch(input, init);
    }
    catch (err) {
        notifyNetworkError('http-web', {
            title: '网络异常',
            message: '请检查网络连接或代理设置。',
        });
        throw err;
    }
    if (debugEnabled()) {
        try {
            console.log('[HTTP][Web] ←', (init?.method || 'GET').toUpperCase(), input, r.status);
        }
        catch { }
    }
    if (!r.ok && (r.status === 0 || r.status >= 500)) {
        notifyNetworkError('http-web', {
            title: '服务不可用',
            message: '服务器暂时无法处理请求，请稍后重试。',
        });
    }
    // Web Response wrapper with resilient body readers
    let cachedText = null;
    async function readTextOnce() {
        if (cachedText !== null)
            return cachedText;
        try {
            cachedText = await r.text();
        }
        catch {
            cachedText = '';
        }
        return cachedText;
    }
    return {
        ok: r.ok,
        status: r.status,
        headers: Object.fromEntries(r.headers),
        async text() {
            return await readTextOnce();
        },
        async json() {
            const t = await readTextOnce();
            try {
                return t ? JSON.parse(t) : undefined;
            }
            catch {
                return undefined;
            }
        },
    };
}
